import { effect, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { AppError, SpinnerService } from '@hglobal/common';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap } from 'rxjs';

import { GetPaginationRes } from '@pp/core/entities';
import {
  PermissionsStore,
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StorageStore,
  withRequestHandler,
  withRequestStatus,
} from '@pp/core/store';
import { DictionariesStore } from '@pp/dictionaries';

import { Rates } from './rate-conditions';
import { ApiSettings } from './shell.entities';
import { SHELL_API } from './shell.interface';

interface ShellState {
  i18Header: string;
  rates: GetPaginationRes<Rates>;
  cryptoMarket: GetPaginationRes<any>;
  totalBalance: string;
  appSettings: ApiSettings;
  telegramLink: string;
}

const initialState: ShellState = {
  i18Header: null,
  rates: null,
  cryptoMarket: null,
  totalBalance: null,
  appSettings: null,
  telegramLink: null,
};

export const ShellStore = signalStore(
  withState(initialState),
  withRequestStatus([
    'getRates',
    'getCryptoMarket',
    'getTotalBalance',
    'getSettings',
    'changeAgentFeatureAvailability',
    'generateTelegramLink',
  ]),
  withRequestHandler(),
  withProps(store => ({
    store,
    shellApi: inject(SHELL_API),
    dictionariesStore: inject(DictionariesStore),
    permissions: inject(PermissionsStore),
    spinnerService: inject(SpinnerService),
    storageStore: inject(StorageStore),
  })),
  withMethods(store => ({
    setHeader: (i18Header: string) => patchState(store, { i18Header }),
    resetChangeAgentFeatureAvailability: () => store.resetRequestStatus('changeAgentFeatureAvailability'),
    resetGenerateTelegramLink: () => store.resetRequestStatus('generateTelegramLink'),
  })),
  withMethods(({ store, shellApi }) => ({
    getRates: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getRates'));
        return shellApi.getRates().pipe(
          tapResponse({
            next: rates => patchState(store, { rates }, setSuccessStatusFor('getRates')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('getRates', error)),
          }),
        );
      }),
    ),
    getCryptoMarket: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getCryptoMarket'));
        return shellApi.getCryptoMarket().pipe(
          tapResponse({
            next: cryptoMarket => patchState(store, { cryptoMarket }, setSuccessStatusFor('getCryptoMarket')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('getCryptoMarket', error)),
          }),
        );
      }),
    ),
    getTotalBalance: rxMethod<number>(
      switchMap(currencyId => {
        patchState(store, setPendingStatusFor('getTotalBalance'));
        return shellApi.getTotalBalance(currencyId).pipe(
          tapResponse({
            next: totalBalance => patchState(store, { totalBalance }, setSuccessStatusFor('getTotalBalance')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('getTotalBalance', error)),
          }),
        );
      }),
    ),
    getSettings: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getSettings'));
        return shellApi.getSettings().pipe(
          tapResponse({
            next: appSettings => patchState(store, { appSettings }, setSuccessStatusFor('getSettings')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('getSettings', error)),
          }),
        );
      }),
    ),
    changeAgentFeatureAvailability: rxMethod<boolean>(
      switchMap(agent_feature_availability => {
        patchState(store, setPendingStatusFor('changeAgentFeatureAvailability'));
        return shellApi.changeAgentFeatureAvailability(agent_feature_availability).pipe(
          tapResponse({
            next: () => patchState(store, setSuccessStatusFor('changeAgentFeatureAvailability')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('changeAgentFeatureAvailability', error)),
          }),
        );
      }),
    ),
    generateTelegramLink: rxMethod<number | unknown>(
      switchMap(orgId => {
        patchState(store, setPendingStatusFor('generateTelegramLink'));
        return shellApi.generateTelegramLink(orgId).pipe(
          tapResponse({
            next: telegramLink => patchState(store, { telegramLink }, setSuccessStatusFor('generateTelegramLink')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('generateTelegramLink', error)),
          }),
        );
      }),
    ),
  })),
  withHooks({
    onInit(store) {
      const settingsPermissions = store.permissions.settingsPermissions;

      store.spinnerService.observe(toObservable(store.isLoading));
      store.getSettings();

      effect(() => {
        if (store.storageStore.orgId()) {
          store.dictionariesStore.getCountries();
        }
      });

      effect(() => {
        if (!settingsPermissions()?.currency_list) {
          return;
        }
        store.dictionariesStore.getCurrencies();
      });
    },
  }),
);
